/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios"

// Instância sem interceptores — usada exclusivamente para o refresh, evitando deadlock circular
const rawApiV2 = axios.create({ baseURL: import.meta.env.VITE_API_URL_V2 })
const refreshToken = (token: string) =>
  rawApiV2.post("/auth/plataforma/refresh-token", { refreshToken: token })

const api = axios.create({ baseURL: import.meta.env.VITE_API_URL })
export const api_v2 = axios.create({ baseURL: import.meta.env.VITE_API_URL_V2 })
export const api_credenciamento = axios.create({
  baseURL: import.meta.env.VITE_API_URL_CREDENCIAMENTO,
})

const EMPRESA_INATIVA_REASONS = ["EMPRESA_INATIVA", "CONTRATO_INATIVO"]

const IMPERSONATION_ENDED_REASONS = ["IMPERSONATION_EXPIRED", "IMPERSONATION_REVOKED"]

export const LOGOUT_REASON_KEY = "logoutReason"

const handleLogout = (reason?: string) => {
  if (reason) sessionStorage.setItem(LOGOUT_REASON_KEY, reason)
  localStorage.removeItem("token")
  localStorage.removeItem("refreshToken")
  localStorage.removeItem("user")
  if (window.location.pathname !== "/login") {
    window.location.href = "/login"
  }
}

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]))
    // Renova 30s antes de expirar para evitar requests rejeitadas por race condition
    return payload.exp * 1000 < Date.now() + 30_000
  } catch {
    return true
  }
}

export const restoreAdminSession = () => {
  const raw = localStorage.getItem(IMPERSONATION_SESSION_KEY)
  if (!raw) return

  try {
    const { user, token, refreshToken } = JSON.parse(raw)
    localStorage.setItem("token", token)
    localStorage.setItem("refreshToken", refreshToken)
    localStorage.setItem("user", JSON.stringify(user))
  } catch {
  } finally {
    localStorage.removeItem(IMPERSONATION_SESSION_KEY)
    window.dispatchEvent(new Event(IMPERSONATION_ENDED_EVENT))
  }
}

const isMissingPesUid = (): boolean => {
  try {
    const storedUser = localStorage.getItem("user")
    if (!storedUser) return false
    return !JSON.parse(storedUser)?.pesUid
  } catch {
    return false
  }
}

export const isImpersonating = (): boolean => !!localStorage.getItem(IMPERSONATION_SESSION_KEY)

export const USER_UPDATED_EVENT = "auth:user-updated"

export const IMPERSONATION_SESSION_KEY = "impersonationAdminSession"
export const IMPERSONATION_ENDED_EVENT = "auth:impersonation-ended"

let pesUidBackfillAttempted = false

let activeRefresh: Promise<string | null> | null = null

const doRefresh = (): Promise<string | null> => {
  if (activeRefresh) return activeRefresh

  activeRefresh = (async () => {
    const storedRefreshToken = localStorage.getItem("refreshToken")

    if (!storedRefreshToken) {
      handleLogout()
      return null
    }

    try {
      const { data } = await refreshToken(storedRefreshToken)
      const newToken: string = data?.data?.token
      const newRefreshToken: string = data?.data?.refreshToken
      const newPesUid: string | undefined = data?.data?.pesUid

      if (!newToken || !newRefreshToken) {
        handleLogout()
        return null
      }

      localStorage.setItem("token", newToken)
      localStorage.setItem("refreshToken", newRefreshToken)

      if (newPesUid) {
        const storedUser = localStorage.getItem("user")
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser)
            if (!userData.pesUid) {
              userData.pesUid = newPesUid
              localStorage.setItem("user", JSON.stringify(userData))
              window.dispatchEvent(new Event(USER_UPDATED_EVENT))
            }
          } catch {
          }
        }
      }

      return newToken
    } catch (err: any) {
      handleLogout(err?.response?.data?.reason)
      return null
    } finally {
      activeRefresh = null
    }
  })()

  return activeRefresh
}

const getValidToken = async (): Promise<string | null> => {
  const token = localStorage.getItem("token")
  if (!token) return null

  if (isImpersonating()) {
    if (isTokenExpired(token)) {
      restoreAdminSession()
      return localStorage.getItem("token")
    }
    return token
  }

  const needsPesUidBackfill = !pesUidBackfillAttempted && isMissingPesUid()
  if (!isTokenExpired(token) && !needsPesUidBackfill) return token

  if (needsPesUidBackfill) pesUidBackfillAttempted = true
  return doRefresh()
}

const attachToken = async (config: InternalAxiosRequestConfig) => {
  const token = await getValidToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
}

const createErrorInterceptor = (instance: AxiosInstance) => async (error: any) => {
  const originalRequest = error.config
  const status = error.response?.status

  if ((status === 401 || status === 403) && !originalRequest._retry) {
    if (isImpersonating()) {
      const impersonationEnded =
        status === 401 || IMPERSONATION_ENDED_REASONS.includes(error.response?.data?.reason)

      if (!impersonationEnded) return Promise.reject(error)

      originalRequest._retry = true
      restoreAdminSession()
      const restoredToken = localStorage.getItem("token")
      if (!restoredToken) return Promise.reject(error)
      originalRequest.headers.Authorization = `Bearer ${restoredToken}`
      return instance(originalRequest)
    }

    originalRequest._retry = true

    const reason = error.response?.data?.reason
    if (EMPRESA_INATIVA_REASONS.includes(reason)) {
      handleLogout(reason)
      return Promise.reject(error)
    }

    const newToken = await doRefresh()
    if (!newToken) return Promise.reject(error)

    originalRequest.headers.Authorization = `Bearer ${newToken}`
    return instance(originalRequest)
  }

  return Promise.reject(error)
}

;[api, api_v2, api_credenciamento].forEach((instance) => {
  instance.interceptors.request.use(attachToken)
  instance.interceptors.response.use((r) => r, createErrorInterceptor(instance))
})

export default api
